import { Injectable, inject } from '@angular/core';

import { FixtureStage, Participant, ParticipantType } from '@cds/betting-offer';
import { ScoreboardSlim } from '@cds/scoreboards/v1';
import {
    EnhancedFixtureViewGroupingConfiguration,
    EventModel,
    EventOptionGroup,
    EventParticipantType,
    ExtendedDisplayType,
    SetTab,
} from '@frontend/sports/betting-offer/feature/model';
import { clone } from 'lodash-es';

import { CommonFixtureFactory } from './common-fixture.factory';
import { DetailedFixtureMarket, DetailedFixtureMarketFactory } from './detailed-fixture-market.factory';
import { ScoreboardFactory } from './scoreboard-factory.service';

@Injectable({ providedIn: 'root' })
export class DetailedUpdateFactory {
    private marketFactory = inject(DetailedFixtureMarketFactory);
    private commonFactory = inject(CommonFixtureFactory);
    private scoreboardFactory = inject(ScoreboardFactory);

    update(model: EventModel, openForBetting: boolean, stage: FixtureStage): void {
        this.commonFactory.update(model, openForBetting, stage);
    }

    updateScoreboard(fixtureScoreboard: ScoreboardSlim, model: EventModel): void {
        // eslint-disable-next-line no-param-reassign
        model.scoreboard = this.scoreboardFactory.createScoreboard(fixtureScoreboard, model);
    }

    updateOptionGroup(
        event: EventModel,
        fixtureMarket: DetailedFixtureMarket,
        fixtureId: string,
        grouping: EnhancedFixtureViewGroupingConfiguration,
    ): void {
        const participants = this.mapParticipants(event);
        const updates = this.marketFactory.create(fixtureMarket, fixtureId, event.online, grouping, participants, event.isPriceBoosted);
        const id = fixtureMarket.id.toString();

        // eslint-disable-next-line no-param-reassign
        event.optionGroups = this.updateMarkets(event.optionGroups, updates, event.online, event.isPriceBoosted);

        if (event.isPriceBoosted) {
            // eslint-disable-next-line no-param-reassign
            event.optionGroups = event.optionGroups.filter((opt) => opt.options.length);
        }
        // eslint-disable-next-line no-param-reassign
        event.marketsDictionary[id] = event.optionGroups.filter((optionGroup) => optionGroup.id === id || optionGroup.groupedMarkets?.includes(id));
    }

    private mapParticipants(event: EventModel): Participant[] | undefined {
        const players = event.players.map(
            (p) =>
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                ({
                    id: p.fixtureParticipantId,
                    image: clone(p.image),
                    name: { short: p.shortName, value: p.name },
                    participantId: p.id,
                    properties: {
                        team: p.teamId,
                        type: ParticipantType.Player,
                    },
                }) as Participant,
        );

        const participants = event.participants
            .filter((x) => x.type === EventParticipantType.Away || x.type === EventParticipantType.Home)
            .map(
                (p) =>
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    ({
                        id: p.id,
                        image: clone(p.image),
                        name: { short: p.shortName, value: p.name },
                        participantId: p.id,
                        properties: {
                            team: p.teamId,
                            type: p.type === EventParticipantType.Home ? ParticipantType.HomeTeam : ParticipantType.AwayTeam,
                        },
                        source: p.source,
                    }) as Participant,
            );

        return [...players, ...participants];
    }

    private updateMarkets(
        currentOptionGroups: EventOptionGroup[],
        updateOptionGroups: EventOptionGroup[],
        fixtureOnline: boolean,
        isPriceBoosted?: boolean,
    ): EventOptionGroup[] {
        updateOptionGroups.forEach((update) => {
            const indexes = currentOptionGroups.reduce(
                (acc: number[], curr: EventOptionGroup, i: number) =>
                    curr.id === update.id &&
                    curr.detailedGrouping.marketSet === update.detailedGrouping.marketSet &&
                    (curr.detailedGrouping.marketSet !== SetTab.Other
                        ? curr.detailedGrouping.marketOrder === update.detailedGrouping.marketOrder
                        : true)
                        ? [...acc, i]
                        : acc,
                [],
            );

            if (!indexes.length) {
                currentOptionGroups.push(update);
            } else {
                indexes.forEach((index) => {
                    // eslint-disable-next-line no-param-reassign
                    currentOptionGroups[index] = this.marketFactory.update(currentOptionGroups[index], update, fixtureOnline, isPriceBoosted);

                    if (!currentOptionGroups[index].options.length) {
                        currentOptionGroups.splice(index, 1);
                    }
                });

                this.updateAutomatedPriceBoostedMarket(currentOptionGroups, update, fixtureOnline);
            }
        });

        return [...currentOptionGroups];
    }

    private updateAutomatedPriceBoostedMarket(
        currentOptionGroups: EventOptionGroup[],
        updateOptionGroup: EventOptionGroup,
        fixtureOnline: boolean,
    ): void {
        const automatedPriceBoostIndex = currentOptionGroups.findIndex(
            (og) => og.id === updateOptionGroup.id && og.detailedGrouping.displayType === ExtendedDisplayType.AutomatedPriceBoost,
        );

        if (automatedPriceBoostIndex > -1) {
            const currentDetailedGrouping = clone(currentOptionGroups[automatedPriceBoostIndex].detailedGrouping);
            // eslint-disable-next-line no-param-reassign
            currentOptionGroups[automatedPriceBoostIndex] = this.marketFactory.update(
                currentOptionGroups[automatedPriceBoostIndex],
                updateOptionGroup,
                fixtureOnline,
                true,
            );
            // eslint-disable-next-line no-param-reassign
            currentOptionGroups[automatedPriceBoostIndex].detailedGrouping = currentDetailedGrouping;
        }
    }
}
